import type { SupervisorVariant } from './view-models';
import type { ReactNode } from 'react';

export interface CapEntry {
  /** Tiers que enxergam a feature (P19 visual). */
  variants: SupervisorVariant[];
  /**
   * Cap de backend real quando existe (consumida via useCapability no PR-T1).
   * R15 verificada vs packages/shared/src/capabilities/capabilities.spec.yaml.
   */
  backendCap?: string;
}

const ALL: SupervisorVariant[] = ['solo', 'team-pro', 'regulated'];
const PRO: SupervisorVariant[] = ['team-pro', 'regulated'];
const REG: SupervisorVariant[] = ['regulated'];

// Subset Tela 1 + chaves de tela (sidebar). Restantes adicionadas onda-a-onda.
// R15 — backendCap corrigidos pós-triangulação:
//   cost-monitoring         → ai-usage-read              (B-1 17/Set/2026: era ai-cost-monitoring)
//   override-approve-type-1 → override-ai-required-validation (Tipo 1 cap; linha 1484)
// Item candidate Q6: auditar cap-by-cap as ~50 chaves vs caps reais de backend.
export const SUPERVISOR_CAP_MATRIX: Record<string, CapEntry> = {
  'sup.tela2.acompanhamento': { variants: PRO, backendCap: 'coattendance-shadow-observer' },
  'sup.tela3.intervencao': { variants: PRO, backendCap: 'force-takeover' },
  'sup.tela4.inbox-time': { variants: PRO, backendCap: 'pendency-team-read' },
  'sup.tela5.sla': { variants: PRO, backendCap: 'sla-read' },
  'sup.tela6.qualidade': { variants: PRO, backendCap: 'quality-aggregations-read' },
  'sup.tela7.centro-alertas': { variants: PRO, backendCap: 'conflict-alert-metrics-read' },
  'sup.tela8.painel-p1': { variants: PRO, backendCap: 'p1-read' },
  // R15: override-approve-type-1 não existe; Tipo 1 = override-ai-required-validation
  'sup.tela9.overrides': { variants: PRO, backendCap: 'override-ai-required-validation' },
  'sup.tela10.insights': { variants: PRO, backendCap: 'ai-insight-supervisor' },
  // Respostas Prontas S4 — métricas de uso + alerta de conduta (cap opt-in).
  'sup.tela11.respostas-prontas': { variants: PRO, backendCap: 'canned-response-analytics' },

  'tela1.kpi.volume-basic': { variants: ALL, backendCap: 'supervisor-dashboard-read' },
  'tela1.kpi.ia-dirigindo': { variants: PRO, backendCap: 'supervisor-dashboard-read' },
  'tela1.kpi.sequences': { variants: PRO },
  'tela1.kpi.conflict-alerts': { variants: PRO, backendCap: 'conflict-alert-metrics-read' },
  'tela1.kpi.awaiting-times': { variants: PRO },
  // R15: métricas em implementação (Item 317) — sem backendCap ainda
  'tela1.kpi.aceite-ia': { variants: PRO },
  'tela1.kpi.retomada-gatilho': { variants: PRO },
  // R15: cost-monitoring não existe; cap real = ai-usage-read (B-1 17/Set/2026)
  'tela1.kpi.custo-ia': { variants: PRO, backendCap: 'ai-usage-read' },

  'tela1.presence-indicator': { variants: ALL },
  'tela1.distribuicao-modos': { variants: PRO },
  'tela1.coattendance': { variants: PRO, backendCap: 'coattendance-shadow-observer' },
  'tela1.coluna-b-priorizadas': { variants: PRO },
  'tela1.coluna-c-alertas': { variants: PRO },
  'tela1.visao-bonds': { variants: PRO },
  'tela1.visao-sequences': { variants: PRO },
  'tela1.audit-findings': { variants: REG },
  'tela1.bond-adversarial': { variants: PRO },
};

/** Gate VISUAL por variant tier (P19). Fail-open: chave desconhecida = visível. */
export function hasVariantCapability(cap: string, variant: SupervisorVariant): boolean {
  const entry = SUPERVISOR_CAP_MATRIX[cap];
  if (entry === undefined) return true;
  return entry.variants.includes(variant);
}

/** Backend cap associada (para o PR-T1 cruzar com useCapability). undefined = só gate de tier. */
export function backendCapFor(cap: string): string | undefined {
  return SUPERVISOR_CAP_MATRIX[cap]?.backendCap;
}

/** Gate visual de componente React por variant tier. Fail-open se `cap` omitido. */
export function Cap({
  cap,
  variant,
  children,
  fallback = null,
}: {
  cap?: string;
  variant: SupervisorVariant;
  children: ReactNode;
  fallback?: ReactNode;
}): ReactNode {
  if (cap === undefined) return children;
  return hasVariantCapability(cap, variant) ? children : fallback;
}
